import { useState } from "react";
import { Link } from "@/lib/router-compat";
import { Helmet } from "react-helmet-async";
import SiteHeader from "@/components/SiteHeader";
import VideoBanner, { SidebarVideoBanners } from "@/components/blackbox/VideoBanner";
import { articles } from "@/data/articles";
import { getPostSlug } from "@/utils/slugUtils";
import { Search, Filter, BookOpen, FileText, ChevronRight } from "lucide-react";

const Articles = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState<string>("all");

  const categories = ["all", ...new Set(articles.map(post => post.category))];

  const filteredPosts = articles.filter(post => {
    const matchesSearch = post.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                          post.content.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = selectedCategory === "all" || post.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  // Schema markup for articles collection
  const collectionSchema = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    "name": "Artigos de Saúde Masculina – PPP, Glândulas de Tyson e Saúde P-nile",
    "description": "Artigos educativos sobre pápulas perladas p-nilianas, glândulas de Tyson, manchas de Fordyce e dúvidas comuns sobre bolinhas no p-nis.",
    "url": "https://smothmethod.com/artigos",
    "mainEntity": {
      "@type": "ItemList",
      "numberOfItems": articles.length,
      "itemListElement": articles.map((post, index) => ({
        "@type": "ListItem",
        "position": index + 1,
        "url": `https://smothmethod.com/artigos/${getPostSlug(post)}`
      }))
    }
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Início",
        "item": "https://smothmethod.com/"
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": "Artigos",
        "item": "https://smothmethod.com/artigos"
      }
    ]
  };

  return (
    <div className="min-h-screen bg-[#f6f7f9]">
      <SidebarVideoBanners />
      <Helmet>
        <title>Artigos: PPP, Glândulas de Tyson e Bolinhas no P-nis</title>
        <meta name="description" content="Artigos educativos sobre PPP, glândulas de Tyson e manchas de Fordyce. Saiba o que é normal e quando procurar avaliação médica." />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://smothmethod.com/artigos" />
        <link rel="alternate" hrefLang="pt-br" href="https://smothmethod.com/artigos" />
        <link rel="alternate" hrefLang="x-default" href="https://smothmethod.com/artigos" />
        <meta property="og:locale" content="pt_BR" />
        
        <meta property="og:type" content="website" />
        <meta property="og:title" content="Artigos sobre Bolinhas no P-nis, PPP e Glândulas de Tyson" />
        <meta property="og:description" content="Artigos educativos sobre PPP, glândulas de Tyson e manchas de Fordyce. Saiba o que é normal." />
        <meta property="og:url" content="https://smothmethod.com/artigos" />
        
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content="Artigos sobre Bolinhas no P-nis e PPP" />
        <meta name="twitter:description" content="Artigos educativos sobre PPP, glândulas de Tyson e manchas de Fordyce." />

        <script type="application/ld+json">
          {JSON.stringify(collectionSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbSchema)}
        </script>
      </Helmet>
      
      <SiteHeader />
      
      <main className="max-w-2xl lg:max-w-3xl mx-auto px-4 py-6">
        {/* Breadcrumb */}
        <nav className="mb-6 text-sm" aria-label="Breadcrumb">
          <ol className="flex items-center gap-2 text-gray-500">
            <li>
              <Link to="/" className="hover:text-[#4267B2]">Início</Link>
            </li>
            <li>/</li>
            <li className="text-gray-700 font-medium">Artigos</li>
          </ol>
        </nav>

        {/* Complete Guide CTA */}
        <Link 
          to="/guias/pequenas-bolinhas-no-penis"
          className="block bg-gradient-to-r from-[#4267B2] to-[#5b7bd5] text-white rounded-lg p-4 mb-6 hover:opacity-95 transition-opacity"
        >
          <div className="flex items-center gap-3">
            <BookOpen className="w-8 h-8" />
            <div>
              <h2 className="font-semibold">📚 Guia Completo: Pequenas Bolinhas no P-nis</h2>
              <p className="text-sm text-white/80">Guia abrangente sobre causas, diferenças e opções de tratamento</p>
            </div>
          </div>
        </Link>

        {/* FAQ Link */}
        <Link 
          to="/perguntas-frequentes"
          className="block bg-white border border-gray-200 rounded-lg p-4 mb-6 hover:shadow-md transition-shadow"
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-[#4267B2]/10 flex items-center justify-center">
              <span className="text-lg">❓</span>
            </div>
            <div>
              <h2 className="font-semibold text-gray-900">Perguntas Frequentes</h2>
              <p className="text-sm text-gray-500">Respostas rápidas sobre PPP, glândulas de Tyson e mais</p>
            </div>
          </div>
        </Link>

        {/* Page Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Artigos de Saúde</h1>
          <p className="text-gray-600 text-sm">
            Conteúdo educativo sobre bolinhas no p-nis, PPP e saúde masculina. Navegue por {articles.length} artigos.
          </p>
        </div>

        {/* Search and Filters */}
        <div className="bg-white rounded-lg border border-gray-200 p-4 mb-6">
          <div className="relative mb-4">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              aria-label="Buscar artigos"
              placeholder="Buscar artigos..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg text-sm focus:outline-hidden focus:ring-2 focus:ring-[#4267B2] focus:border-transparent"
            />
          </div>

          <div className="flex flex-wrap gap-2">
            {categories.map(category => (
              <button
                key={category}
                onClick={() => setSelectedCategory(category)}
                className={`px-3 py-1.5 text-xs font-medium rounded-full transition-colors ${
                  selectedCategory === category
                    ? "bg-[#4267B2] text-white"
                    : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                }`}
              >
                {category === "all" ? "Todos os Tópicos" : category}
              </button>
            ))}
          </div>
        </div>

        {/* Results Count */}
        <div className="text-sm text-gray-500 mb-4 flex items-center gap-2">
          <FileText className="w-4 h-4" />
          Exibindo {filteredPosts.length} de {articles.length} artigos
        </div>

        {/* Articles List */}
        <div className="space-y-3">
          {filteredPosts.map(post => (
            <Link
              key={post.id}
              to={`/artigos/${getPostSlug(post)}`}
              className="block bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow group"
            >
              <div className="flex items-center gap-2 mb-2">
                <span className="text-xs font-medium text-[#4267B2] bg-[#4267B2]/10 px-2 py-0.5 rounded">
                  {post.category}
                </span>
              </div>
              <h2 className="font-semibold text-gray-900 mb-2 group-hover:text-[#4267B2] transition-colors">
                {post.title}
              </h2>
              <div className="flex items-center justify-between text-xs text-gray-500">
                <span>Artigo educativo</span>
                <ChevronRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </div>
            </Link>
          ))}
        </div>

        {/* No Results */}
        {filteredPosts.length === 0 && (
          <div className="text-center py-12 bg-white rounded-lg border border-gray-200">
            <Search className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">Nenhum artigo encontrado</h3>
            <p className="text-gray-500 text-sm">
              Tente ajustar a busca ou os filtros para encontrar o que procura.
            </p>
          </div>
        )}

        {/* Footer */}
        <footer className="text-center py-6 mt-8 border-t border-gray-200">
          <p className="text-xs text-gray-500">
            Este conteúdo é apenas para fins educativos. Sempre consulte um profissional médico em caso de dúvidas de saúde.
          </p>
        </footer>

        <VideoBanner />
      </main>
    </div>
  );
};

export default Articles;
